import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
    FaSearch,
    FaUserPlus,
    FaEdit,
    FaTrash,
    FaFileExcel,
    FaUpload,
    FaSync,
    FaTimes,
    FaCheckCircle,
    FaExclamationTriangle,
    FaChevronLeft,
    FaChevronRight
} from "react-icons/fa";
import API_BASE_URL from "../../../../config/api";
import { useAuth } from "../../../../context/AuthContext";

function Students() {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentStudent, setCurrentStudent] = useState({
        studentId: "",
        userId: "",
        rollNo: "",
        name: "",
        department: "",
        college: "",
        year: "",
        email: ""
    });
    const [uploadedStudents, setUploadedStudents] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState({ type: "", message: "" });

    // Fetch students when page or search changes
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchStudents();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [page, search]);

    const fetchStudents = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/students/admin/groups/students`, {
                params: {
                    page: page,
                    limit: 9,
                    search: search
                },
                headers: {
                    Authorization: `Bearer ${user?.token}`
                }
            });

            if (response.data && Array.isArray(response.data.students)) {
                setStudents(response.data.students);
                setTotalPages(response.data.totalPages);
            } else {
                setStudents([]);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching students:", error);
            setIsLoading(false);
            setSaveStatus({
                type: "error",
                message: "Failed to load students: " + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleDelete = async (studentId) => {
        if (!window.confirm("Are you sure you want to delete this student?")) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/api/students/students/${studentId}`, {
                headers: {
                    Authorization: `Bearer ${user?.token}`
                }
            });

            fetchStudents();

            setSaveStatus({
                type: "success",
                message: "Student deleted successfully"
            });

            setTimeout(() => setSaveStatus({ type: "", message: "" }), 3000);
        } catch (error) {
            console.error("Error deleting student:", error);
            setSaveStatus({
                type: "error",
                message: "Failed to delete student: " + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleEdit = (student) => {
        setCurrentStudent({ ...student });
        setShowEditModal(true);
    };

    const handleSave = async () => {
        try {
            // Enhanced validation to check all required fields
            if (!currentStudent.studentId || !currentStudent.userId ||
                !currentStudent.rollNo || !currentStudent.name ||
                !currentStudent.department || !currentStudent.college ||
                !currentStudent.year || !currentStudent.email) {

                setSaveStatus({
                    type: "error",
                    message: "Please fill in all required fields"
                });
                return;
            }

            if (currentStudent._id) {
                // Update existing student
                await axios.put(
                    `${API_BASE_URL}/api/students/students/${currentStudent._id}`,
                    currentStudent,
                    {
                        headers: {
                            Authorization: `Bearer ${user?.token}`
                        }
                    }
                );

                setSaveStatus({
                    type: "success",
                    message: "Student updated successfully"
                });
            } else {
                // Create new student
                await axios.post(
                    `${API_BASE_URL}/api/students/students`,
                    currentStudent,
                    {
                        headers: {
                            Authorization: `Bearer ${user?.token}`
                        }
                    }
                );

                setSaveStatus({
                    type: "success",
                    message: "Student added successfully"
                });
            }

            setShowEditModal(false);
            fetchStudents();
            setTimeout(() => setSaveStatus({ type: "", message: "" }), 3000);
        } catch (error) {
            console.error("Error saving student:", error);
            setSaveStatus({
                type: "error",
                message: "Failed to save student: " + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentStudent({ ...currentStudent, [name]: value });
    };

    const handleAddStudent = () => {
        const newStudent = {
            studentId: "",
            userId: "",
            rollNo: "",
            name: "",
            department: "",
            college: "",
            year: "",
            email: "",
        };
        setCurrentStudent(newStudent);
        setShowEditModal(true);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(sheet);

                    // Basic validation could be added here
                    setUploadedStudents(jsonData);
                    setUploadError(null);
                } catch (error) {
                    setUploadError("Error processing file. Please check the format.");
                    console.error(error);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleSubmitStudents = async () => {
        try {
            if (!uploadedStudents) {
                setUploadError("Please upload a file first");
                return;
            }

            const response = await axios.post(
                `${API_BASE_URL}/api/students/students`,
                { students: uploadedStudents },
                {
                    headers: {
                        Authorization: `Bearer ${user?.token}`
                    }
                }
            );

            if (response.status === 201) {
                setSaveStatus({
                    type: "success",
                    message: "Students data uploaded successfully!"
                });

                setUploadedStudents(null);
                setUploadError(null);

                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) {
                    fileInput.value = "";
                }

                fetchStudents();

                setTimeout(() => setSaveStatus({ type: "", message: "" }), 3000);
            }
        } catch (error) {
            setUploadError(
                error.response?.data?.message || "Error uploading students data"
            );
            console.error("Error uploading students:", error);
        }
    };

    return (
        <div className="min-h-screen p-6 md:p-12 font-sans">
            <div className="max-w-7xl mx-auto">
                <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">
                            Students Directory
                        </h1>
                        <p className="text-gray-500 mt-2">Manage student records and enrollments</p>
                    </div>
                </header>

                {/* Alerts */}
                {saveStatus.message && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 shadow-sm animate-fade-in ${saveStatus.type === "success"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                        }`}>
                        {saveStatus.type === "success" ? <FaCheckCircle /> : <FaExclamationTriangle />}
                        {saveStatus.message}
                    </div>
                )}

                {/* Actions Bar */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8 flex flex-col lg:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full lg:max-w-md">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search Students by Name, Roll No..."
                            value={search}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] transition-all"
                        />
                    </div>

                    <div className="flex gap-3 w-full lg:w-auto">
                        <button
                            onClick={handleAddStudent}
                            className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-[#b7202e] hover:bg-[#901a24] text-white px-5 py-2.5 rounded-lg transition-colors shadow-md shadow-red-100 font-medium"
                        >
                            <FaUserPlus /> Add Student
                        </button>
                        <button
                            onClick={() => fetchStudents()}
                            className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-lg transition-colors"
                            title="Refresh List"
                        >
                            <FaSync className={isLoading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* Upload Section */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FaFileExcel className="text-green-600" /> Bulk Upload
                    </h3>
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                        <div className="flex-1 w-full">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <FaUpload className="w-8 h-8 mb-3 text-gray-400" />
                                    <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                    <p className="text-xs text-gray-500">XLSX, XLS or CSV</p>
                                </div>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileUpload}
                                />
                            </label>
                        </div>

                        {uploadedStudents && (
                            <div className="w-full md:w-auto flex flex-col gap-3 animate-fade-in">
                                <div className="p-4 bg-green-50 rounded-lg border border-green-100 text-green-700 text-sm font-medium">
                                    {uploadedStudents.length} students loaded and ready.
                                </div>
                                <button
                                    onClick={handleSubmitStudents}
                                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors shadow-sm"
                                >
                                    Submit to Database
                                </button>
                            </div>
                        )}
                    </div>
                    {uploadError && (
                        <div className="mt-3 text-red-500 text-sm flex items-center gap-2">
                            <FaExclamationTriangle /> {uploadError}
                        </div>
                    )}
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {isLoading && !students.length ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#b7202e] rounded-full animate-spin mb-4"></div>
                            <p>Loading records...</p>
                        </div>
                    ) : students.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-sm uppercase tracking-wider">
                                            <th className="p-4 font-semibold">Student ID</th>
                                            <th className="p-4 font-semibold">Roll No</th>
                                            <th className="p-4 font-semibold">Name</th>
                                            <th className="p-4 font-semibold">Department</th>
                                            <th className="p-4 font-semibold">Year</th>
                                            <th className="p-4 font-semibold">Email</th>
                                            <th className="p-4 font-semibold text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {students.map((item) => (
                                            <tr key={item._id} className="hover:bg-red-50/30 transition-colors">
                                                <td className="p-4 text-gray-700 font-medium">{item.studentId}</td>
                                                <td className="p-4 text-gray-600">{item.rollNo}</td>
                                                <td className="p-4 text-gray-800 font-semibold">{item.name}</td>
                                                <td className="p-4 text-gray-600">
                                                    <span className="inline-block px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">
                                                        {item.department}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-600">{item.year}</td>
                                                <td className="p-4 text-gray-500 text-sm">{item.email}</td>
                                                <td className="p-4">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Edit"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item._id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={page === 1 || isLoading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaChevronLeft className="w-3 h-3" />
                                    Previous
                                </button>
                                <span className="text-sm text-gray-600">
                                    Page <span className="font-semibold text-gray-900">{page}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    disabled={page === totalPages || isLoading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                    <FaChevronRight className="w-3 h-3" />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="p-12 text-center text-gray-500 bg-gray-50">
                            <FaSearch className="text-4xl mx-auto mb-3 text-gray-300" />
                            <p>No students found matching your search.</p>
                        </div>
                    )}
                </div>

                {/* Edit Modal */}
                {showEditModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                                <h3 className="text-xl font-bold text-gray-800">
                                    {currentStudent._id ? "Edit Student" : "Add New Student"}
                                </h3>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Student ID <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="studentId"
                                            value={currentStudent.studentId}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">User ID <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="userId"
                                            value={currentStudent.userId}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Roll Number <span className="text-red-500">*</span></label>
                                        <input
                                            type="number"
                                            name="rollNo"
                                            value={currentStudent.rollNo}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Name <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={currentStudent.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Department <span className="text-red-500">*</span></label>
                                        <select
                                            name="department"
                                            value={currentStudent.department}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] bg-white"
                                            required
                                        >
                                            <option value="">Select Department</option>
                                            <option value="Computer Engineering">Computer Engineering</option>
                                            <option value="Information Technology">Information Technology</option>
                                            <option value="Electronics and Computer Engineering">Electronics and Computer Engineering</option>
                                            <option value="Electronics and Telecommunication Engineering">Electronics and Telecommunication Engineering</option>
                                            <option value="Mechanical Engineering">Mechanical Engineering</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">College <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="college"
                                            value={currentStudent.college}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Year <span className="text-red-500">*</span></label>
                                        <select
                                            name="year"
                                            value={currentStudent.year}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] bg-white"
                                            required
                                        >
                                            <option value="">Select Year</option>
                                            <option value="FY">FY</option>
                                            <option value="SY">SY</option>
                                            <option value="TY">TY</option>
                                            <option value="LY">LY</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2 col-span-1 md:col-span-2">
                                        <label className="text-sm font-semibold text-gray-700">Email <span className="text-red-500">*</span></label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={currentStudent.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-5 py-2.5 rounded-lg bg-[#b7202e] text-white hover:bg-[#901a24] transition-colors shadow-sm font-medium"
                                >
                                    {currentStudent._id ? "Save Changes" : "Add Student"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Students;
